// Componente RegisterScreen - Flujo completo de registro
// Reutiliza la lógica y estética de amarsi-app optimizado con React Query

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LayoutSignUp from '../../components/UI/LayoutSignUp';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import PasswordInput from '../../components/UI/PasswordInput';
import Textarea from '../../components/UI/Textarea';
import ItemRadio from '../../components/UI/ItemRadio';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';
import { ages } from '../../constants/humanQualities';
import ReCAPTCHA from 'react-google-recaptcha';

/**
 * Componente RegisterScreen
 * 
 * Flujo de registro multi-paso optimizado con React Query
 * Basado en el diseño y lógica de amarsi-app
 */
const RegisterScreen = () => {
  const navigate = useNavigate();
  
  // Hook de autenticación para registro
  const { register, isRegistering } = useAuth();

  // Estados del formulario
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [years, setYears] = useState(18);
  const [sex, setSex] = useState(null);
  const [buscando, setBuscando] = useState(null);
  const [preferencia, setPreferencia] = useState(0);
  const [currentLocation, setCurrentLocation] = useState([-34.6036926, -58.3815382]);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');

  // Estados de control del flujo
  const [checkTerminos, setCheckTerminos] = useState(false);
  // const [checkPoliticas, setCheckPoliticas] = useState(true); // Reservado para uso futuro
  const [checkGenero, setCheckGenero] = useState(false);
  const [checkPreferencia, setCheckPreferencia] = useState(0);
  const [checkEdad, setCheckEdad] = useState(false);
  const [checkSmallBio, setCheckSmallBio] = useState(false);
  const [checkBuscando, setCheckBuscando] = useState(false);

  // Refs para inputs
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const fullNameInputRef = useRef(null);
  const bioInputRef = useRef(null);
  const captchaRef = useRef(null);

  // Perfil del usuario para registro
  const miPerfil = {
    full_name: fullName,
    bio: bio,
    sex: sex,
    sex_pref: preferencia,
    years: years,
    enbuscade: buscando || 0,
    altura: 0,
    peso: 0,
    complexion: 0,
    situacionamorosa: 0,
    pronombre: 0,
    lat: currentLocation[0], 
    lng: currentLocation[1],
    membershipType: 200
  };

  // Obtiene la ubicación actual del usuario
  const getCurrentLocation = async () => {
    try {
      if (!navigator.geolocation) {
        console.log('Geolocalización no soportada por este navegador');
        return;
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      console.log('Ubicación obtenida:', [latitude, longitude]);
      setCurrentLocation([latitude, longitude]);

    } catch (error) {
      console.log('Error al obtener ubicación:', error);
      // Usar ubicación por defecto (Buenos Aires)
      setCurrentLocation([-34.6036926, -58.3815382]);
    }
  };

  // Maneja el cambio del reCAPTCHA
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaError(''); // Limpiar error cuando se completa correctamente
  };

  // Maneja el error del reCAPTCHA
  const handleCaptchaError = () => {
    console.error('Error en reCAPTCHA: Verifica que la clave del sitio sea correcta y esté configurada para este dominio');
    setCaptchaToken(null);
    setCaptchaError('Error en el reCAPTCHA. Por favor, recarga la página.');
  };

  // Maneja cuando expira el reCAPTCHA
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setCaptchaError('El reCAPTCHA ha expirado. Por favor, complétalo nuevamente.');
  };

  // Maneja el registro de usuario usando el hook de autenticación
  const handleSignup = async () => {
    try {
      // Validar que el reCAPTCHA ha sido completado
      if (!captchaToken) {
        setCaptchaError('Por favor, completa el reCAPTCHA antes de continuar');
        return;
      }

      // Usar el hook de autenticación con email y password, además de los datos adicionales
      await register(email, password, miPerfil);
      
      // El registro exitoso redirige automáticamente según el contexto
      navigate('/auth/login');
      
    } catch (error) {
      console.error('Error en el registro:', error);
      // El error ya es manejado por el hook de autenticación
    }
  };

  // Efecto para obtener ubicación al montar
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Paso 1: Términos y condiciones
  if (!checkTerminos) {
    return (
      <LayoutSignUp withProgressBar step={1}>
        <div className="p-6">
          <div className="text-center mb-6">
            <AmarsiLogo width="80" height="80" className="mx-auto mb-4" />
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Condiciones generales de uso
            </h2>
            
            <div className="space-y-3 text-gray-600 dark:text-gray-300">
              <p>
                Amarsi recopila y utiliza tus datos al momento de tu inscripción y cuando usas la aplicación con el fin de:
              </p>
              
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Proporcionarte nuestros servicios y asistencia;</li>
                <li>Enviarte, contenido promocional pertinente, principalmente publicidad de los productos y servicios de Amarsi y/o sus socios comerciales;</li>
              </ul>
              
              <p className="text-sm">
                Los datos recolectados son destinados a Amarsi y sus socios comerciales localizados dentro y fuera de la Unión Europea. Tienes derecho de acceso, rectificación y eliminación de los datos que te conciernen y, de ser necesario, derecho de oposición a la portabilidad de tus datos, de limitación del tratamiento de los mismos y la facultad de definir las directivas relativas al destino de tus datos.
              </p>
            </div>
          </div>
        </div>

      <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700">
          <Button
            onClick={() => setCheckTerminos(true)}
            className="w-full"
            variant="primary"
            size="md"
          >
            Siguiente
          </Button>
          <div className="text-center mt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              ya tenés cuenta?
              <button 
                onClick={() => navigate('/auth/login')}
                className="text-primary-600 hover:text-primary-500 font-medium ml-2"
              >
                Iniciar sesión
              </button>
            </p>
          </div>

        </div>
      </LayoutSignUp>
    );
  }

  // Paso 3: Género
  if (!checkGenero) {
    return (
      <LayoutSignUp withProgressBar step={3} showBackButton onBackPress={() => { setCheckTerminos(false); setSex(null); }}>
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            ¿Con qué género te identificas?
          </h2>

          <div className="space-y-3">
            <p className="text-gray-600 dark:text-gray-300">
              Elige el que mejor represente tu identidad.
            </p>

            <div className="space-y-3">
              <ItemRadio 
                active={sex === 1} 
                labelText="Soy una Mujer" 
                onPress={() => setSex(1)}
              />
              <ItemRadio 
                active={sex === 2} 
                labelText="Soy un Hombre" 
                onPress={() => setSex(2)}
              />
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
          <Button
            onClick={() => { setCheckTerminos(false); setSex(null); }}
            className="px-6"
            variant="secondary"
          >
            Atrás
          </Button>

          <Button
            onClick={() => setCheckGenero(true)}
            disabled={sex < 1}
            className={`px-6 ${sex > 0 ? '' : 'opacity-50 cursor-not-allowed'}`}
            variant={sex > 0 ? 'primary' : 'secondary'}
          >
            Siguiente
          </Button>
        </div>
      </LayoutSignUp>
    );
  }

  // Paso 4: Preferencia
  if (!checkPreferencia) {
    return (
      <LayoutSignUp withProgressBar step={4} showBackButton onBackPress={() => { setCheckGenero(false); setPreferencia(0); }}>
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            ¿A quién te gustaría conocer?
          </h2>

          <div className="space-y-3">
            <p className="text-gray-600 dark:text-gray-300">
              Elige una opción y cámbiala en cualquier momento.
            </p>

            <div className="space-y-3">
              <ItemRadio 
                active={preferencia === 0} 
                labelText="Todos" 
                onPress={() => setPreferencia(0)}
              />
              <ItemRadio 
                active={preferencia === 1} 
                labelText="Mujeres" 
                onPress={() => setPreferencia(1)}
              />
              <ItemRadio 
                active={preferencia === 2} 
                labelText="Hombres" 
                onPress={() => setPreferencia(2)}
              />
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
          <Button
            onClick={() => { setCheckGenero(false); setPreferencia(0); }}
            className="px-6"
            variant="secondary"
          >
            Atrás
          </Button>

          <Button
            onClick={() => setCheckPreferencia(true)}
            className="px-6"
            variant="primary"
          >
            Siguiente
          </Button>
        </div>
      </LayoutSignUp>
    );
  }

  // Paso 5: Edad
  if (!checkEdad) {
    return (
      <LayoutSignUp withProgressBar step={5} showBackButton onBackPress={() => { setCheckPreferencia(false); setYears(18); }}>
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            ¿Cuántos años tienes?
          </h2>
          
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Para poder mostrarte a las personas adecuadas, necesitamos saber tu edad.
            </p>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-700 dark:text-gray-300 font-medium">Edad</span>
              
              <select
                value={years}
                onChange={(e) => setYears(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                {ages.map((age) => (
                  <option key={age} value={age}>
                    {age} años
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
          <Button
            onClick={() => { setCheckPreferencia(false); setYears(18); }}
            className="px-6"
            variant="secondary"
          >
            Atrás
          </Button>

          <Button
            onClick={() => setCheckEdad(true)}
            disabled={years < 17}
            className={`px-6 ${years > 17 ? '' : 'opacity-50 cursor-not-allowed'}`}
            variant={years > 17 ? 'primary' : 'secondary'}
          >
            Siguiente
          </Button>
        </div>
      </LayoutSignUp>
    );
  }

  // Paso 6: Qué buscas
  if (!checkBuscando) {
    return (
      <LayoutSignUp withProgressBar step={6} showBackButton onBackPress={() => { setCheckEdad(false); setBuscando(null); }}>
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            ¿Qué estás buscando?
          </h2>
          
          <div className="space-y-3">
            <p className="text-gray-600 dark:text-gray-300">
              La honestidad es la clave para encontrar lo que estás buscando en Amarsi.
            </p>

            <div className="space-y-3">
              <ItemRadio 
                active={buscando === 0} 
                labelText="Sin respuesta" 
                onPress={() => setBuscando(0)}
              />
              <ItemRadio 
                active={buscando === 1} 
                labelText="Lo que surja" 
                onPress={() => setBuscando(1)}
              />
              <ItemRadio 
                active={buscando === 2} 
                labelText="Una relación" 
                onPress={() => setBuscando(2)}
              />
              <ItemRadio 
                active={buscando === 3} 
                labelText="Algo casual" 
                onPress={() => setBuscando(3)}
              />
              <ItemRadio 
                active={buscando === 4} 
                labelText="Aún no lo sé" 
                onPress={() => setBuscando(4)}
              />
              <ItemRadio 
                active={buscando === 5} 
                labelText="Prefiero no decirlo" 
                onPress={() => setBuscando(5)}
              />
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
          <Button
            onClick={() => { setCheckEdad(false); setBuscando(null); }}
            className="px-6"
            variant="secondary"
          >
            Atrás
          </Button>

          <Button
            onClick={() => setCheckBuscando(true)}
            className="px-6"
            variant="primary"
          >
            Siguiente
          </Button>
        </div>
      </LayoutSignUp>
    );
  }

  // Paso 7: Información del perfil
  if (!checkSmallBio) {
    return (
      <LayoutSignUp withProgressBar step={7} showBackButton onBackPress={() => { setCheckBuscando(false); setFullName(''); setBio(''); }}>
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Agregá información a tu perfil
          </h2>

          <div className="space-y-6">
            {/* Nombre de pantalla usando componente Input reutilizable */}
            <div>
              <Input
                ref={fullNameInputRef}
                type="text"
                placeholder="Todos verán esto en la cuadrícula"
                value={fullName}
                maxLength={16}
                onChange={(e) => setFullName(e.target.value)}
                label="Nombre de pantalla"
                helperText={`${fullName.length}/16`}
                icon="user"
              />
              
              {fullName.length > 4 && (
                <p className="text-green-600 dark:text-green-400 text-sm mt-2">
                  ¡Lindo nombre 😊!
                </p>
              )}
            </div>

            {/* Biografía usando componente Textarea reutilizable */}
            <Textarea
              ref={bioInputRef}
              placeholder="Lo que la gente debería saber de ti..."
              value={bio}
              maxLength={250}
              rows={4}
              onChange={(e) => setBio(e.target.value)}
              label="Acerca de mí"
              helperText={`${bio.length}/250`}
            />
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
          <Button
            onClick={() => { setCheckBuscando(false); setFullName(''); setBio(''); }}
            className="px-6"
            variant="secondary"
          >
            Atrás
          </Button>

          <Button
            onClick={() => setCheckSmallBio(true)}
            disabled={fullName.length < 4}
            className={`px-6 ${fullName.length >= 4 ? '' : 'opacity-50 cursor-not-allowed'}`}
            variant={fullName.length >= 4 ? 'primary' : 'secondary'}
          >
            Siguiente
          </Button>
        </div>
      </LayoutSignUp>
    );
  }

  // Paso 8: Credenciales finales
  return (
    <LayoutSignUp withProgressBar step={8} showBackButton onBackPress={() => { setCheckSmallBio(false); setEmail(''); setPassword(''); }}>
      <div className="p-6">
        <div className="text-center mb-6">
          <AmarsiLogo width="80" height="80" className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Creá tu cuenta
          </h2>
        </div>

        <div className="space-y-4">
          {/* Campo Email usando componente Input reutilizable */}
          <Input
            ref={emailInputRef}
            type="email"
            placeholder="Tu correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            icon="email"
            autoFocus
          />

          {/* Campo Contraseña usando componente PasswordInput reutilizable */}
          <PasswordInput
            ref={passwordInputRef}
            placeholder="Tu contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            showStrengthIndicator={false}
          />

          {/* Validación de longitud de contraseña */}
          {password.length > 0 && password.length < 8 && (
            <div className="text-amber-600 dark:text-amber-400 text-sm">
              • Debe tener más de 8 caracteres.
            </div>
          )}

          {/* reCAPTCHA de Google */}
          <div className="flex justify-center">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              onErrored={handleCaptchaError}
              onExpired={handleCaptchaExpired}
            />
          </div>

          {/* Mensaje de error del reCAPTCHA */}
          {captchaError && (
            <div className="text-red-600 dark:text-red-400 text-sm text-center mt-2">
              {captchaError}
            </div>
          )}

          {/* Términos y condiciones */}
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Usamos tu dirección de correo electrónico para administrar tu cuenta e informarte sobre novedades y ofertas especiales. Puedes oponerte en cualquier momento haciendo click en el enlace para darte de baja. Al continuar, aceptas también nuestras Normas de la Comunidad y consientes al procesamiento de tu ubicación para que podamos mostrarte personas interesantes cerca de ti.
          </div>
        </div>
      </div>

      <div className="bg-neutral-50 dark:bg-neutral-800 p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
        <Button
          onClick={() => { setCheckSmallBio(false); setEmail(''); setPassword(''); }}
          className="px-6"
          variant="secondary"
        >
          Atrás
        </Button>

        <Button
          onClick={handleSignup}
          loading={isRegistering}
          disabled={password.length < 8 || email.length < 8 || !captchaToken}
          className={`px-6 ${password.length >= 8 && email.length >= 8 && captchaToken ? '' : 'opacity-50 cursor-not-allowed'}`}
          variant={password.length >= 8 && email.length >= 8 && captchaToken ? 'primary' : 'secondary'}
        >
          Crear cuenta
        </Button>
      </div>
    </LayoutSignUp>
  );
};

export default RegisterScreen;
